import React, { cloneElement, ReactElement, useState } from 'react';

import { WizardContainer } from './WizardContainer';
import { WizardNavigation } from './WizardNavigation';
import { WizardStepDefinition, WizardSteps } from './WizardSteps';

interface WizardProps {
  title: string;
  description: string;
  steps: WizardStepDefinition[];
  children: ReactElement[];
  onComplete: (data: Record<string, any>) => void | Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  initialData?: Record<string, any>;
  maxWidth?:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl';
}

export const Wizard: React.FC<WizardProps> = ({
  title,
  description,
  steps,
  children,
  onComplete,
  onCancel,
  isLoading = false,
  initialData = {},
  maxWidth,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [wizardData, setWizardData] =
    useState<Record<string, any>>(initialData);
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>(
    {},
  );

  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;

  const updateStepData = (stepData: Record<string, any>) => {
    setWizardData((prev) => ({ ...prev, ...stepData }));
  };

  const markStepComplete = (stepIndex: number, isValid = true) => {
    setStepValidation((prev) => ({ ...prev, [stepIndex]: isValid }));
    if (isValid) {
      setCompletedSteps((prev) => new Set([...prev, stepIndex]));
    } else {
      setCompletedSteps((prev) => {
        const newSet = new Set(prev);
        newSet.delete(stepIndex);
        return newSet;
      });
    }
  };

  const handleNext = async () => {
    if (isLastStep) {
      // Submit the form
      await onComplete(wizardData);
    } else {
      // Move to next step
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const canProceed = stepValidation[currentStep] !== false;

  // Clone children with additional props
  const currentChild = children[currentStep];
  const childWithProps = currentChild
    ? cloneElement(currentChild, {
        wizardData,
        updateStepData,
        markStepComplete: (isValid: boolean) =>
          markStepComplete(currentStep, isValid),
        isLoading,
        onCancel,
      })
    : null;

  return (
    <WizardContainer
      title={title}
      description={description}
      maxWidth={maxWidth}
    >
      <WizardSteps
        steps={steps}
        currentStep={currentStep}
        completedSteps={completedSteps}
      />

      {/* Current step caption */}
      <div className="mb-4 text-center">
        <span className="text-sm text-gray-500">
          {steps[currentStep]?.title}
        </span>
      </div>

      <div className="min-h-96">{childWithProps}</div>

      <WizardNavigation
        currentStep={currentStep}
        totalSteps={totalSteps}
        isLoading={isLoading}
        canProceed={canProceed}
        onPrevious={handlePrevious}
        onNext={handleNext}
        showPrevious={currentStep > 0}
      />
    </WizardContainer>
  );
};
